import { Checkbox } from 'antd5';
import { startCase } from 'lodash';
import { useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { TooltipWithQuestionIcon, useMessage, useNotification } from 'components/ui';
import {
  type AtlasGqlTask,
  AtlasGqlDispatchMode,
  useSubscribeToNotificationsForWorkContainerMutation,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useFeatures } from 'utils/features';

import { BulkNotificationSubscriptionButton } from './BulkNotificationSubscriptionButton';
import { DeclineSubscriptionButton } from './DeclineSubscriptionButton';
import { NotificationSubscriptionButton } from './NotificationSubscriptionButton';
import { ButtonContainer, CheckboxLabel, PromptContainer } from './NotificationSubscriptions.style';
import { useUpdateAutoSubscribePreference } from './useUpdateAutoSubscribePreference';
import {
  getParentCampaignLink,
  getParentWorkOrderLink,
  isParentCampaignSubscribed,
  isParentWorkOrderSubscribed,
} from './utils';

type PromptParams = {
  content: JSX.Element;
  workContainer: Partial<AtlasGqlTask> & { __typename?: string };
  containerIdsForBulkSubscription?: string[];
};

type Result = {
  openPrompt: (params: PromptParams) => void;
};

export const useWCNotificationSubscriptionPrompt = (): Result => {
  const { t } = useTranslation();
  const { WORK_CONTAINER_NOTIFICATIONS } = useFeatures().features;
  const { user: accountUser } = useAccountContext();
  const { updateAutoSubscribePreference } = useUpdateAutoSubscribePreference();
  // `null` is the product default: prompt per container. Do not inherit a
  // previous user's local "remember my selection" value.
  const preferredAutoSubscribe =
    accountUser?.preferences?.autoSubscribeWorkContainerNotifications ?? null;

  const saveSubscriptionSelection = useRef<boolean>(false);

  const notification = useNotification();
  const message = useMessage();

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
      skip: !WORK_CONTAINER_NOTIFICATIONS,
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe] = useSubscribeToNotificationsForWorkContainerMutation({});

  /**
   * If the user has notifications enabled, has the release toggle, and is not already
   * subscribed to a parent container, prompt them to subscribe to notifications
   * Otherwise, just display the success message.
   */
  const openPrompt = useCallback(
    ({ content, workContainer, containerIdsForBulkSubscription }: PromptParams) => {
      if (WORK_CONTAINER_NOTIFICATIONS && notificationsEnabled) {
        const handleDeclineSubscription = (notificationKey: string) => {
          if (saveSubscriptionSelection.current) {
            updateAutoSubscribePreference(false, { silent: true });
          }
          setTimeout(() => {
            notification.destroy(notificationKey);
          }, 2000);
        };

        const afterSubscribe = (notificationKey: string) => {
          if (saveSubscriptionSelection.current) {
            updateAutoSubscribePreference(true, { silent: true });
          }
          setTimeout(() => {
            notification.destroy(notificationKey);
          }, 2000);
        };

        const shouldAutoSubscribe = preferredAutoSubscribe === true;
        const shouldSkipSubscribeOption = preferredAutoSubscribe === false;

        const parentCampaignSubscribed = isParentCampaignSubscribed(workContainer);
        const parentWorkOrderSubscribed = isParentWorkOrderSubscribed(workContainer);

        const automaticSubscriptionContent = (
          <>
            {t(
              'workOrder.autoSubscribedNotification',
              'You were automatically subscribed to Notifications'
            )}{' '}
            <TooltipWithQuestionIcon
              message={
                <Trans
                  i18nKey="workOrder.autoSubscribedTooltip"
                  defaults="You previously opted into automatic Subscriptions. This can be changed in <a>settings</a> any time"
                  components={{
                    /* eslint-disable jsx-a11y/anchor-has-content */
                    a: (
                      <a href="/settings/notification-settings" target="_blank" rel="noreferrer" />
                    ),
                    /* eslint-enable jsx-a11y/anchor-has-content */
                  }}
                />
              }
            />
          </>
        );

        if (shouldSkipSubscribeOption) {
          message.success(content);
        } else if (containerIdsForBulkSubscription) {
          if (shouldAutoSubscribe) {
            containerIdsForBulkSubscription.map(containerId =>
              subscribe({ variables: { input: { containerId } } })
            );
            notification.success({
              key: 'bulkTaskCreationSuccess',
              message: null,
              description: (
                <PromptContainer>
                  {content}
                  <br />
                  {automaticSubscriptionContent}
                </PromptContainer>
              ),
              placement: 'top',
            });
          } else {
            notification.success({
              key: 'bulkTaskCreationSuccess',
              message: null,
              description:
                parentCampaignSubscribed || parentWorkOrderSubscribed ? (
                  <PromptContainer>
                    {content}
                    <br />
                    <Trans
                      i18nKey="workOrder.subscribedToParentBulk"
                      defaults="You are subscribed to Notifications for <link/>, which these Tasks are in, and will receive Notifications about changes."
                      components={{
                        link: parentCampaignSubscribed
                          ? getParentCampaignLink(workContainer, true)
                          : getParentWorkOrderLink(workContainer, true),
                      }}
                    />
                  </PromptContainer>
                ) : (
                  <>
                    <PromptContainer>
                      {content}
                      <br />
                      <>
                        {t('workOrder.subscribePromptTaskCount', {
                          count: containerIdsForBulkSubscription.length,
                        })}
                      </>
                    </PromptContainer>
                    <Checkbox
                      defaultChecked={saveSubscriptionSelection.current}
                      onChange={e => {
                        saveSubscriptionSelection.current = e.target.checked;
                      }}
                      name="Remember my selection"
                    >
                      <CheckboxLabel>
                        {t('workOrder.rememberMySelection', 'Remember my selection')}{' '}
                      </CheckboxLabel>
                      <TooltipWithQuestionIcon
                        message={
                          <Trans
                            i18nKey="workOrder.futureSubscriptionPreferenceTooltip"
                            defaults="When you create work containers in the future, your subscription preference will be applied. This can be changed in <a>settings</a> any time"
                            components={{
                              /* eslint-disable jsx-a11y/anchor-has-content */
                              a: (
                                <a
                                  href="/settings/notification-settings"
                                  target="_blank"
                                  rel="noreferrer"
                                />
                              ),
                              /* eslint-enable jsx-a11y/anchor-has-content */
                            }}
                          />
                        }
                      />
                    </Checkbox>
                    <ButtonContainer>
                      <DeclineSubscriptionButton
                        onClick={() => handleDeclineSubscription('bulkTaskCreationSuccess')}
                      />
                      <BulkNotificationSubscriptionButton
                        containerIds={containerIdsForBulkSubscription}
                        afterSubscribe={() => afterSubscribe('bulkTaskCreationSuccess')}
                      />
                    </ButtonContainer>
                  </>
                ),
              placement: 'top',
              duration: 8,
            });
          }
        } else {
          if (shouldAutoSubscribe) {
            subscribe({ variables: { input: { containerId: workContainer.id } } });
            notification.success({
              key: workContainer.id,
              message: null,
              description: (
                <PromptContainer>
                  {content}
                  <br />
                  {automaticSubscriptionContent}
                </PromptContainer>
              ),
              placement: 'top',
            });
          } else {
            const workContainerTypeName =
              workContainer.type?.name ??
              startCase(workContainer.__typename) ??
              t('workOrder.workContainer', 'Work Container');

            notification.success({
              key: workContainer.id,
              message: null,
              description:
                parentCampaignSubscribed || parentWorkOrderSubscribed ? (
                  <PromptContainer>
                    {content}
                    <br />
                    <Trans
                      i18nKey="workOrder.subscribedToParentSingle"
                      defaults="You are subscribed to Notifications for <link/>, which this {{type}} is in, and will receive Notifications about changes."
                      components={{
                        link: parentCampaignSubscribed
                          ? getParentCampaignLink(workContainer, true)
                          : getParentWorkOrderLink(workContainer, true),
                      }}
                      values={{ type: workContainerTypeName }}
                    />
                  </PromptContainer>
                ) : (
                  <>
                    <PromptContainer>
                      {content}
                      <br />
                      <>{t('workOrder.subscribePromptSingle', { type: workContainerTypeName })}</>
                    </PromptContainer>
                    <Checkbox
                      data-testid="subscription-remember-selection"
                      defaultChecked={saveSubscriptionSelection.current}
                      onChange={e => {
                        saveSubscriptionSelection.current = e.target.checked;
                      }}
                      name="Remember my selection"
                    >
                      <CheckboxLabel>
                        {t('workOrder.rememberMySelection', 'Remember my selection')}{' '}
                      </CheckboxLabel>
                      <TooltipWithQuestionIcon
                        message={
                          <Trans
                            i18nKey="workOrder.futureSubscriptionPreferenceTooltip"
                            defaults="When you create work containers in the future, your subscription preference will be applied. This can be changed in <a>settings</a> any time"
                            components={{
                              /* eslint-disable jsx-a11y/anchor-has-content */
                              a: (
                                <a
                                  href="/settings/notification-settings"
                                  target="_blank"
                                  rel="noreferrer"
                                />
                              ),
                              /* eslint-enable jsx-a11y/anchor-has-content */
                            }}
                          />
                        }
                      />
                    </Checkbox>
                    <ButtonContainer>
                      <DeclineSubscriptionButton
                        onClick={() => handleDeclineSubscription(workContainer.id)}
                      />
                      <NotificationSubscriptionButton
                        containerId={workContainer.id}
                        afterSubscribe={() => afterSubscribe(workContainer.id)}
                      />
                    </ButtonContainer>
                  </>
                ),
              placement: 'top',
              duration: 8,
            });
          }
        }
      } else {
        message.success(content);
      }
    },
    [
      WORK_CONTAINER_NOTIFICATIONS,
      notificationsEnabled,
      preferredAutoSubscribe,
      saveSubscriptionSelection.current,
      t,
      updateAutoSubscribePreference,
    ]
  );

  return {
    openPrompt,
  };
};
